/**
 * LegacyRedirect — small <Navigate replace> wrapper that signals the
 * banner primitive a redirect just happened.
 *
 * Why
 * ----
 * Slice 91 (audit GAP-MIGRATION-SAFETY-LEGACY) front-runs the §17.5 /
 * §17.9 / §17.10 IA consolidation. Bookmarks to retired paths must
 * redirect to the canonical surface AND inform the user the page
 * moved — without blocking the redirect or piling up additional
 * navigation steps.
 *
 * Contract
 * --------
 *   - Renders nothing visible.
 *   - On mount: writes `LEGACY_REDIRECT_FLAG_KEY` to sessionStorage with
 *     the legacy path + banner text the banner primitive will read.
 *   - Returns `<Navigate to={newPath} replace />` synchronously so the
 *     browser updates the URL and component tree atomically.
 *
 * SRP
 * ---
 * This component owns ONLY the "I just redirected from a legacy path"
 * sessionStorage handshake. The banner primitive owns rendering; the
 * SSOT (`legacyRedirects.ts`) owns the table.
 *
 * exactOptionalPropertyTypes: `bannerText` is `?:` and we never pass
 * `undefined` explicitly.
 */

import { Navigate } from 'react-router-dom';
import { captureError } from '../errors/captureError';
import { LEGACY_REDIRECT_FLAG_KEY } from './legacyRedirects';

export type LegacyRedirectProps = {
    /** Canonical destination — set by `main.tsx` from the SSOT row. */
    to: string;
    /** The legacy path being redirected from (used by the banner). */
    from: string;
    /** Resolved banner copy — may be the SSOT override or the generic
     *  fallback (`resolveBannerText` is the SRP-respecting helper). */
    bannerText: string;
};

type LegacyRedirectFlag = {
    from: string;
    to: string;
    bannerText: string;
};

function writeFlag(flag: LegacyRedirectFlag): void {
    try {
        sessionStorage.setItem(LEGACY_REDIRECT_FLAG_KEY, JSON.stringify(flag));
    } catch (err) {
        // sessionStorage may throw on quota / disabled-storage Safari
        // private mode. The redirect itself MUST not be blocked by the
        // banner handshake — swallow the write error, log via the
        // standard captureError seam, and continue. The banner simply
        // won't show; the redirect still happens.
        captureError(err, { scope: 'LegacyRedirect', op: 'write-flag' });
    }
}

function LegacyRedirect({ to, from, bannerText }: LegacyRedirectProps) {
    // Write the handshake INSIDE the render path (not in an effect) so
    // the banner primitive sees the flag on its very first render after
    // <Navigate replace> commits — no inter-frame gap, no "banner
    // flashes in then out" artefact. Idempotent: re-rendering the same
    // <LegacyRedirect> writes the same payload.
    writeFlag({ from, to, bannerText });
    return <Navigate to={to} replace />;
}

export default LegacyRedirect;
